import { numFont } from '../../utils/fonts';
import * as React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import { useTranslation } from 'react-i18next';
import { normalizeLanguage } from '../../utils/languages';
import { ALL_WEEK_DAYS, UNAVAILABILITY_CONFIG, UI_COLORS } from './calendarConstants';
import { formatHours, isAllDayAbsence, absenceSpanText } from './hooks/monthUtils';
import { parseDateKey } from './hooks/dateUtils';
import { TooltipRow } from './EventTooltipContent';

const LANE_HEIGHT = 46;
const NAME_COLUMN = 170;
const TOTAL_COLUMN = 96;
// A thin column, not a square: the bar's height is the message, its width is not.
const BAR_WIDTH = 9;
// Days off are drawn, but narrow, so the work week gets the room.
const DAY_OFF_SHARE = 0.45;
// Bays have no colour of their own in the payload; one slate for all, numbered.
const BAY_COLOR = '#4a5568';
const NO_BAY_LANE_ID = 'none';

function barColor(booked, available) {
  if (available <= 0) return '#ef4444';
  const ratio = booked / available;
  if (ratio > 1) return '#ef4444';
  if (ratio >= 0.875) return '#3182ce';
  return '#63b3ed';
}

function percentColor(percent) {
  if (percent === null) return 'text.disabled';
  if (percent > 100) return '#c53030';
  if (percent >= 90) return '#b45309';
  return 'text.secondary';
}

/**
 * The hover card of one lane cell, in the event tooltips' dress: who (or which bay), which
 * day, the hours, the work orders in a bay, and the absence if any.
 */
function LaneHoverCard({ lane, day, load, absence, absenceText, awayAllDay, language }) {
  const { t } = useTranslation();
  const date = parseDateKey(day.date);
  const weekday = ALL_WEEK_DAYS[(date.getDay() + 6) % 7];
  return (
    <Box sx={{ p: 1.5, minWidth: 160 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '13px', mb: 0.75 }}>{lane.name}</Typography>
      <TooltipRow icon={AccessTimeIcon}>
        {t(weekday.shortKey)} {date.getDate()}.{date.getMonth() + 1}.
        {!awayAllDay && load.available > 0 && ` · ${formatHours(load.booked, language)} / ${formatHours(load.available, language)} h`}
        {!awayAllDay && load.available <= 0 && load.booked > 0 && ` · ${formatHours(load.booked, language)} h`}
        {load.workOrders > 0 && ` · ${t('calendar.month.cell.workOrders', { count: load.workOrders })}`}
      </TooltipRow>
      {absence && <TooltipRow icon={BeachAccessIcon}>{absenceText}</TooltipRow>}
    </Box>
  );
}

/**
 * Rows are mechanics (or service bays), columns the days of the month: who has room, and
 * who is away. Each cell is a thin bar on a track the height of the shift (for a bay, the
 * workshop's work day), so the empty part of the track is the room; a fill past the track
 * is a double booking. The last column is the lane's month.
 */
function MonthLaneGrid({ monthData, selectedDate, onSelect, mode = 'employee' }) {
  const { t, i18n } = useTranslation();
  const language = normalizeLanguage(i18n?.language);
  const days = React.useMemo(() => (monthData.days || []).filter((day) => day.in_month), [monthData]);
  // One lookup table per day instead of a .find() per cell (30 mechanics × 31 days).
  const byDay = React.useMemo(() => {
    const map = {};
    days.forEach((day) => {
      const perEmployee = {};
      (day.per_employee || []).forEach((row) => {
        perEmployee[row.employee_id] = row;
      });
      const perBay = {};
      (day.per_bay || []).forEach((row) => {
        perBay[row.bay_id] = row;
      });
      const absences = {};
      (day.absences || []).forEach((row) => {
        if (!absences[row.employee_id]) absences[row.employee_id] = row;
      });
      map[day.date] = { perEmployee, perBay, absences };
    });
    return map;
  }, [days]);
  const isBayMode = mode === 'bay';
  // Bays get a lane each, plus one for the work that has no bay, when there is any.
  const lanes = React.useMemo(() => {
    if (!isBayMode) return monthData.employees || [];
    const bays = (monthData.bays || []).map((bay, index) => ({ id: bay.id, name: bay.name, initials: String(index + 1), color: BAY_COLOR }));
    if (days.some((day) => day.no_bay?.work_orders > 0)) {
      bays.push({ id: NO_BAY_LANE_ID, name: t('calendar.month.lanes.noBay'), initials: '–', color: '#a0aec0', noBay: true });
    }
    return bays;
  }, [isBayMode, monthData, days, t]);

  // What a lane holds on a day: a mechanic's shift, or a bay's share of the work day.
  const loadOf = (lane, day) => {
    const index = byDay[day.date] || { perEmployee: {}, perBay: {}, absences: {} };
    if (!isBayMode) {
      const row = index.perEmployee[lane.id];
      return { booked: row?.booked || 0, available: row?.available || 0, workOrders: 0 };
    }
    if (lane.noBay) {
      return { booked: day.no_bay?.booked || 0, available: 0, workOrders: day.no_bay?.work_orders || 0, noTrack: true };
    }
    const row = index.perBay[lane.id];
    return { booked: row?.booked || 0, available: day.bay_hours || 0, workOrders: row?.work_orders || 0 };
  };
  const columns = [
    `${NAME_COLUMN}px`,
    ...days.map((day) => (day.is_workday ? 'minmax(0, 1fr)' : `minmax(0, ${DAY_OFF_SHARE}fr)`)),
    `${TOTAL_COLUMN}px`,
  ].join(' ');

  const absenceLetter = (type) => {
    const config = UNAVAILABILITY_CONFIG[type] || UNAVAILABILITY_CONFIG.personal;
    return t(config.labelKey).charAt(0).toUpperCase();
  };

  // The lane's month: booked and available over the in-month days.
  const totals = React.useMemo(() => {
    const map = {};
    lanes.forEach((lane) => {
      map[lane.id] = { booked: 0, available: 0 };
      days.forEach((day) => {
        const load = loadOf(lane, day);
        map[lane.id].booked += load.booked;
        map[lane.id].available += load.available;
      });
      map[lane.id].percent = map[lane.id].available > 0 ? Math.round((map[lane.id].booked / map[lane.id].available) * 100) : null;
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lanes, days, byDay]);

  if (lanes.length === 0) {
    return (
      <Box data-testid="calendar-month-lanes" data-mode={mode} sx={{ p: 4, textAlign: 'center', color: 'text.disabled', fontSize: '13px' }}>
        {isBayMode ? t('calendar.month.noBays') : t('calendar.month.noEmployees')}
      </Box>
    );
  }

  const headerCell = { py: '6px', textAlign: 'center', borderLeft: 1, borderColor: 'divider', lineHeight: 1.2 };

  return (
    <Box data-testid="calendar-month-lanes" data-mode={mode} sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: '12px', overflow: 'hidden' }}>
      {/* day header: weekday over the day number, today filled */}
      <Box sx={{ display: 'grid', gridTemplateColumns: columns, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
        <Typography
          component="div"
          sx={{ p: '9px 12px', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em', color: 'text.secondary', textTransform: 'uppercase', alignSelf: 'center' }}
        >
          {isBayMode ? t('Service bays') : t('Employees')}
        </Typography>
        {days.map((day) => {
          const date = parseDateKey(day.date);
          const weekday = ALL_WEEK_DAYS[(date.getDay() + 6) % 7];
          const muted = !day.is_workday || day.is_past;
          return (
            <Box
              key={day.date}
              sx={{
                ...headerCell,
                color: day.is_today ? 'white' : muted ? 'text.disabled' : 'text.secondary',
                bgcolor: day.is_today ? 'secondary.main' : 'transparent',
              }}
            >
              <Box component="div" sx={{ fontSize: '9px', fontWeight: 600, textTransform: 'lowercase', opacity: day.is_today ? 0.9 : 1 }}>
                {t(weekday.shortKey)}
              </Box>
              <Box component="div" sx={{ fontSize: '11px', fontWeight: day.is_today ? 700 : 600, ...numFont }}>
                {date.getDate()}
              </Box>
            </Box>
          );
        })}
        <Typography
          component="div"
          sx={{ ...headerCell, fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em', color: 'text.secondary', textTransform: 'uppercase', alignSelf: 'center', py: '9px' }}
        >
          {t('Month')}
        </Typography>
      </Box>

      {lanes.map((lane, rowIndex) => {
        const total = totals[lane.id] || { booked: 0, available: 0, percent: null };
        return (
          <Box
            key={lane.id}
            data-testid={isBayMode ? `month-bay-lane-${lane.id}` : `month-lane-${lane.id}`}
            sx={{ display: 'grid', gridTemplateColumns: columns, borderBottom: rowIndex < lanes.length - 1 ? 1 : 0, borderColor: '#edf2f7' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, height: LANE_HEIGHT, minWidth: 0 }}>
              <Box
                component="span"
                sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: lane.color, color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10.5px', fontWeight: 700, flexShrink: 0 }}
              >
                {lane.initials}
              </Box>
              <Typography component="span" sx={{ fontSize: '12.5px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {lane.name}
              </Typography>
            </Box>
            {days.map((day) => {
              const load = loadOf(lane, day);
              const absence = isBayMode ? null : (byDay[day.date]?.absences || {})[lane.id] || null;
              const absenceConfig = absence ? UNAVAILABILITY_CONFIG[absence.type] || UNAVAILABILITY_CONFIG.personal : null;
              // A two-hour errand does not empty the day: only an absence that takes the whole
              // shift (or a day with no room left) replaces the bar; a shorter one is a badge
              // in the corner and the bar shows the hours the mechanic still has.
              const awayAllDay = Boolean(absence) && (isAllDayAbsence(absence) || load.available <= 0);
              const off = !absence && load.available <= 0 && load.booked <= 0;
              // The "no bay" lane has no capacity to draw a track for: a bar of the hours alone.
              const trackless = Boolean(load.noTrack);
              const overbooked = load.booked > load.available;
              const heightPct = load.available > 0 ? Math.min(100, (load.booked / load.available) * 100) : load.booked > 0 ? 100 : 0;
              const selected = selectedDate === day.date;
              const absenceText = absence
                ? `${t(absenceConfig.labelKey)}${absenceSpanText(absence) ? ` ${absenceSpanText(absence)}` : ''}`
                : '';
              return (
                <Tooltip
                  key={day.date}
                  title={<LaneHoverCard lane={lane} day={day} load={load} absence={absence} absenceText={absenceText} awayAllDay={awayAllDay} language={language} />}
                  placement="top"
                  arrow
                  enterDelay={500}
                  enterNextDelay={200}
                  slotProps={{
                    tooltip: { sx: { bgcolor: 'background.paper', color: 'text.primary', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', borderRadius: '10px', p: 0, maxWidth: 320 } },
                    arrow: { sx: { color: 'background.paper' } },
                  }}
                >
                  <Box
                    onClick={() => onSelect(day)}
                    sx={{
                      borderLeft: 1,
                      borderColor: '#edf2f7',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      p: '5px 2px',
                      height: LANE_HEIGHT,
                      boxSizing: 'border-box',
                      cursor: 'pointer',
                      position: 'relative',
                      bgcolor: awayAllDay ? absenceConfig.bg : off ? '#fbfcfd' : selected ? '#e8f2fd' : day.is_today ? UI_COLORS.todayBg : 'background.paper',
                      boxShadow: selected ? 'inset 0 0 0 2px #3182ce' : 'none',
                      // Days gone by fade, so an empty past column does not read as room.
                      opacity: day.is_past ? 0.45 : 1,
                    }}
                  >
                    {awayAllDay ? (
                      <Box component="span" sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 800, color: absenceConfig.color, letterSpacing: '0.03em' }}>
                        {absenceLetter(absence.type)}
                      </Box>
                    ) : !off ? (
                      /* The track is the shift; the fill is what is booked; the rest is the room. */
                      <Box sx={{ width: BAR_WIDTH, height: '100%', bgcolor: load.available > 0 ? '#edf2f7' : 'transparent', borderRadius: '3px', display: 'flex', alignItems: 'flex-end', overflow: 'hidden' }}>
                        {load.booked > 0 && (
                          <Box sx={{ width: '100%', height: trackless ? `${Math.min(100, (load.booked / (day.bay_hours || 8)) * 100)}%` : `${heightPct}%`, minHeight: 3, borderRadius: '3px', bgcolor: trackless ? '#a0aec0' : barColor(load.booked, load.available) }} />
                        )}
                      </Box>
                    ) : null}
                    {overbooked && !awayAllDay && !trackless && (
                      <Box sx={{ position: 'absolute', top: 3, left: '50%', ml: '-2px', width: 4, height: 4, borderRadius: '50%', bgcolor: '#c53030' }} />
                    )}
                    {absence && !awayAllDay && (
                      <Box
                        component="span"
                        data-testid={`month-lane-partial-absence-${lane.id}-${day.date}`}
                        sx={{ position: 'absolute', top: 2, right: 2, minWidth: 12, height: 12, px: '2px', borderRadius: '3px', bgcolor: absenceConfig.bg, color: absenceConfig.color, fontSize: '8px', fontWeight: 800, lineHeight: '12px', textAlign: 'center' }}
                      >
                        {absenceLetter(absence.type)}
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
            <Box
              data-testid={`month-lane-total-${lane.id}`}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', px: 1.25, height: LANE_HEIGHT, borderLeft: 1, borderColor: '#edf2f7', lineHeight: 1.2 }}
            >
              <Box component="span" sx={{ fontSize: '12px', fontWeight: 700, color: percentColor(total.percent), ...numFont }}>
                {total.percent === null ? (lane.noBay ? `${formatHours(total.booked, language)} h` : '–') : `${total.percent} %`}
              </Box>
              {!lane.noBay && (
                <Box component="span" sx={{ fontSize: '10px', color: 'text.disabled', whiteSpace: 'nowrap' }}>
                  {formatHours(total.booked, language)} / {formatHours(total.available, language)} h
                </Box>
              )}
            </Box>
          </Box>
        );
      })}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1.75, py: 1.25, borderTop: 1, borderColor: 'divider', bgcolor: '#fbfcfd', fontSize: '11px', color: 'text.secondary', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 6, height: 16, bgcolor: '#edf2f7', borderRadius: '2px', display: 'flex', alignItems: 'flex-end', overflow: 'hidden' }}>
            <Box sx={{ width: '100%', height: '45%', bgcolor: '#63b3ed' }} />
          </Box>
          {t('calendar.month.lanes.partlyBooked')}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 6, height: 16, bgcolor: '#3182ce', borderRadius: '2px' }} />
          {t('calendar.month.lanes.full')}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 6, height: 16, bgcolor: '#ef4444', borderRadius: '2px' }} />
          {t('calendar.month.band.over')}
        </Box>
        {!isBayMode && Object.entries(UNAVAILABILITY_CONFIG).map(([key, config]) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box component="span" sx={{ width: 14, height: 14, bgcolor: config.bg, color: config.color, borderRadius: '3px', fontSize: '8.5px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              {absenceLetter(key)}
            </Box>
            {t(config.labelKey)}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default MonthLaneGrid;
